/**
 * Dashboard Statistics Cards Component
 * Displays 4 stat cards: Total Companies, Active Companies, Total Users, Pending Approvals
 */
import React from 'react';
import { Card } from 'antd';
import {
  ApartmentOutlined,
  CheckCircleOutlined,
  TeamOutlined,
  ExclamationCircleOutlined,
  RiseOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { AnalyticsOverview } from '../../services/superAdminService';
import './DashboardStatsCards.css';

interface StatCardProps {
  title: string;
  value: number;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
  growth?: {
    value: string;
    icon?: React.ReactNode;
  };
  percentage?: string;
  progress: number; // 0-100
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  color,
  growth,
  percentage,
  progress,
}) => {
  return (
    <Card className="dashboard-stat-card" style={{ borderColor: color }}>
      <div className="stat-card-content">
        <div className="stat-card-header">
          <div className="stat-card-icon" style={{ backgroundColor: color }}>
            {icon}
          </div>
          <div className="stat-card-indicator">
            {growth && (
              <div className="growth-indicator" style={{ backgroundColor: '#e6f8ed' }}>
                {growth.icon || <RiseOutlined />}
                <span style={{ color: '#50B450', fontSize: '12px', fontWeight: 600 }}>
                  {growth.value}
                </span>
              </div>
            )}
            {percentage && (
              <div className="percentage-indicator" style={{ backgroundColor: '#e6f8ed' }}>
                <span style={{ color: '#50B450', fontSize: '12px', fontWeight: 600 }}>
                  {percentage}
                </span>
              </div>
            )}
            {!growth && !percentage && (
              <div className="action-indicator" style={{ backgroundColor: '#fff3e0' }}>
                <ClockCircleOutlined style={{ color: '#F09628', fontSize: '12px' }} />
                <span style={{ color: '#F09628', fontSize: '12px', fontWeight: 600 }}>
                  Action
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="stat-card-body">
          <div className="stat-card-title">{title}</div>
          <div className="stat-card-value">{value}</div>
          <div className="stat-card-subtitle">{subtitle}</div>
        </div>
        <div className="stat-card-progress">
          <div
            className="stat-card-progress-bar"
            style={{
              backgroundColor: color,
              width: `${progress}%`,
            }}
          />
        </div>
      </div>
    </Card>
  );
};

interface DashboardStatsCardsProps {
  overview?: AnalyticsOverview;
  loading?: boolean;
}

const DashboardStatsCards: React.FC<DashboardStatsCardsProps> = ({ overview, loading = false }) => {
  if (loading || !overview) {
    return (
      <div className="dashboard-stats-cards">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} loading={true} className="dashboard-stat-card" />
        ))}
      </div>
    );
  }

  const activePercentage = overview.total_companies > 0 
    ? Math.round((overview.active_companies / overview.total_companies) * 100)
    : 0;

  const userGrowthPercentage = overview.total_users > 0
    ? Math.round((overview.users_this_month / overview.total_users) * 100)
    : 0;

  return (
    <div className="dashboard-stats-cards">
      <StatCard
        title="Total Companies"
        value={overview.total_companies}
        subtitle={`+${overview.companies_this_month} new this month`}
        icon={<ApartmentOutlined />}
        color="#2878F0"
        growth={{ value: `${overview.monthly_growth_rate >= 0 ? '+' : ''}${overview.monthly_growth_rate.toFixed(1)}%` }}
        progress={Math.min(100, (overview.total_companies / 100) * 100)}
      />
      <StatCard
        title="Active Companies"
        value={overview.active_companies}
        subtitle={`${overview.inactive_companies} inactive`}
        icon={<CheckCircleOutlined />}
        color="#50B450"
        percentage={`${activePercentage}%`}
        progress={activePercentage}
      />
      <StatCard
        title="Total Users"
        value={overview.total_users}
        subtitle={`+${overview.users_this_month} new this month`}
        icon={<TeamOutlined />}
        color="#8C3CC8"
        growth={{ value: `${userGrowthPercentage > 0 ? '+' : ''}${userGrowthPercentage}%` }}
        progress={Math.min(100, (overview.total_users / 1000) * 100)}
      />
      <StatCard
        title="Total Locations"
        value={overview.total_locations}
        subtitle={`${overview.active_locations} active`}
        icon={<ExclamationCircleOutlined />}
        color="#F09628"
        progress={overview.total_locations > 0 ? Math.min(100, (overview.active_locations / overview.total_locations) * 100) : 0}
      />
    </div>
  );
};

export default DashboardStatsCards;
